// src/components/Convert/Convert.js
import React, { useState, useRef, useEffect } from 'react';
import './Convert.css';

const Convert = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("Select Language");
  const [codeInput, setCodeInput] = useState("");
  const [convertedCode, setConvertedCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null); // Reference for dropdown

  const languages = [
    "Ada", "ALGOL", 
    "Apex", "Assembly Language", 
    "AWK", "Bash", 
    "C", "C#", 
    "C++", "Clojure", 
    "COBOL", "Crystal", 
    "Dart", "Delphi/Object Pascal", 
    "Elixir", "Elm", 
    "Erlang", "F#", 
    "Forth", "Fortran", 
    "Go", "Groovy", 
    "Haskell", "HTML/CSS", 
    "Java", "JavaScript", 
    "Julia", "Kotlin", 
    "Lisp", "Lua", 
    "MATLAB", "Objective-C", 
    "Pascal", "Perl", 
    "PHP", "Prolog", 
    "Python", "R", 
    "Ruby", "Rust", 
    "Scala", "Scheme", 
    "Shell", "Smalltalk", 
    "SQL", "Swift", 
    "TypeScript", "VBA", 
    "Visual Basic .NET", "Wolfram"
];

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setIsOpen(false); // Close dropdown after selection
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleConvert = async () => {
    if (selectedLanguage === "Select Language" || !codeInput.trim()) {
      alert("Please select a language and enter some code to convert.");
      return;
    }
  
    setIsLoading(true);
    setConvertedCode(""); // Clear previous output
    
    try {
      const response = await fetch("https://codetocode-backend.onrender.com/api/convert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          codeToConvert: codeInput,
          language: selectedLanguage,
        }),
      });
  
      if (!response.ok) {
        console.error("Failed to convert code");
        setIsLoading(false);
        return;
      }
  
      // Create a reader to handle the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let hasReceivedFirstChunk = false;
  
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
  
        // Decode and append each chunk to the convertedCode state
        const chunk = decoder.decode(value, { stream: true });
        
        // Stop loading after receiving the first non-empty chunk
        if (!hasReceivedFirstChunk && chunk.trim().length > 0) {
          setIsLoading(false);
          hasReceivedFirstChunk = true;
        }
        
        setConvertedCode((prev) => prev + chunk);
      }
    } catch (error) {
      console.error("Error during conversion:", error);
      setIsLoading(false);
    }
  };
  
  return (
    <div className="convert-container">
      <div className="top-bar">
        <div
          className={`custom-dropdown ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          ref={dropdownRef} // Attach ref to dropdown
        >
          {selectedLanguage}
          {isOpen && (
            <div className="dropdown-options">
              {languages.map((language, index) => (
                <div 
                  key={index} 
                  className="dropdown-option"
                  onClick={() => handleLanguageSelect(language)}
                >
                  {language}
                </div>
              ))}
            </div>
          )}
        </div>
        <button 
          className={`convert-button ${isLoading ? 'loading' : ''}`} 
          onClick={handleConvert}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="spinner-container">
              <div className="spinner"></div>
              <span>Converting...</span>
            </div>
          ) : (
            'Convert'
          )}
        </button>
      </div>
      <div className="input-output">
        <textarea 
          className="input-box" 
          placeholder="Enter your code here..." 
          value={codeInput} 
          onChange={(e) => setCodeInput(e.target.value)} 
        />
        <textarea 
          className="output-box" 
          placeholder="Translated code will appear here..." 
          value={convertedCode} 
          readOnly 
        />
      </div>
    </div>
  );
};

export default Convert;